/**
 * Export a playbook by rendering the whole book (every section + page) into a
 * clean, print-ready window and triggering the browser's print dialog — from
 * which the user can Save as PDF or print. Printing the on-screen HTML is the
 * most reliable path (avoids the fragile PDF-library route) and keeps the
 * book's real formatting.
 */
const escapeHtml = (s = "") =>
  String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

export const exportPlaybook = (book, { businessName, primaryColor, secondaryColor, gradient } = {}) => {
  if (!book) return;
  const brand = primaryColor || "#1b598e";
  const brand2 = secondaryColor || brand;
  const coverBg = gradient ? `linear-gradient(135deg, ${brand} 0%, ${brand2} 100%)` : brand;
  const sections = Array.isArray(book.sections) ? book.sections : [];

  const pagesHtml = sections
    .map((sec) =>
      (sec.pages || [])
        .map((pg) => {
          const hasContent = pg.content && pg.content.trim();
          const inner = hasContent ? pg.content : `<h1>${escapeHtml(pg.title || "")}</h1>`;
          return `<article class="pg">${inner}</article>`;
        })
        .join("")
    )
    .join("");

  const tocHtml = sections
    .map((sec) => {
      const pageItems = (sec.pages || [])
        .map((pg) => `<li>${escapeHtml(pg.title || "Untitled page")}</li>`)
        .join("");
      return `<div class="toc-sec"><div class="toc-sec-t">${escapeHtml(sec.title || "Section")}</div><ul>${pageItems}</ul></div>`;
    })
    .join("");

  const doc = `<!doctype html>
<html>
<head>
<meta charset="utf-8" />
<title>${escapeHtml(book.title || "Playbook")}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&display=swap');
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: 'DM Sans', ui-sans-serif, system-ui, sans-serif; color: #1d2939; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  .cover { padding: 96px 64px 72px; background: ${coverBg}; color: #fff; }
  .cover .kicker { display: inline-block; font-size: 12px; font-weight: 700; letter-spacing: .14em; text-transform: uppercase; color: #fff; background: rgba(255,255,255,0.18); border-radius: 30px; padding: 6px 14px; margin: 0 0 16px; }
  .cover h1 { font-size: 40px; font-weight: 800; letter-spacing: -0.02em; color: #fff; margin: 0 0 8px; }
  .cover p { font-size: 15px; color: rgba(255,255,255,0.9); margin: 0; }
  .toc { padding: 40px 64px; }
  .toc h2 { font-size: 13px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: #98a2b3; margin: 0 0 16px; }
  .toc-sec { margin-bottom: 14px; }
  .toc-sec-t { font-size: 15px; font-weight: 600; color: #0f172a; margin-bottom: 4px; }
  .toc-sec ul { margin: 0 0 0 18px; padding: 0; }
  .toc-sec li { font-size: 13.5px; color: #475467; line-height: 1.7; }
  .pg { padding: 48px 64px; page-break-before: always; }
  .pg h1 { font-size: 26px; font-weight: 600; letter-spacing: -0.02em; color: #0f172a; margin: 0 0 10px; }
  .pg h2 { font-size: 18px; font-weight: 600; color: #0f172a; margin: 24px 0 8px; }
  .pg h3 { font-size: 15px; font-weight: 600; color: #0f172a; margin: 18px 0 6px; }
  .pg p, .pg li { font-size: 14px; line-height: 1.75; color: #344054; }
  .pg ul, .pg ol { padding-left: 22px; margin: 0 0 12px; }
  .pg blockquote { border-left: 3px solid ${brand}; background: rgba(0,0,0,0.02); margin: 0 0 14px; padding: 10px 16px; border-radius: 0 8px 8px 0; color: #344054; }
  .pg table { border-collapse: collapse; width: 100%; margin: 0 0 14px; font-size: 13px; }
  .pg th { background: ${brand}; color: #fff; text-align: left; padding: 8px 10px; }
  .pg td { border: 1px solid #eaecf0; padding: 8px 10px; }
  .pg a { color: ${brand}; }
  .pg mark { background: #fff3bf; padding: 0 2px; }
  .pg img { display: block; max-width: 100%; height: auto; border-radius: 10px; margin: 12px 0; }
  .pg figure[data-image-placeholder] { display: none; }
  @media print {
    .cover { padding-top: 80px; }
    .no-print { display: none; }
  }
</style>
</head>
<body>
  <header class="cover">
    <p class="kicker">${escapeHtml(businessName || "Company")} · Playbook</p>
    <h1>${escapeHtml(book.title || "Playbook")}</h1>
    <p>${escapeHtml(book.type || "Handbook")}</p>
  </header>
  ${tocHtml ? `<nav class="toc"><h2>Contents</h2>${tocHtml}</nav>` : ""}
  ${pagesHtml}
</body>
</html>`;

  const w = window.open("", "_blank");
  if (!w) return; // popup blocked — user can allow popups and retry
  w.document.open();
  w.document.write(doc);
  w.document.close();
  w.focus();
  // Let fonts/layout settle, then open the print dialog.
  setTimeout(() => {
    try {
      w.print();
    } catch (e) {
      /* user can still print manually */
    }
  }, 500);
};

export default exportPlaybook;
